// The review/grading family of the Terminal console (#4435 step 4, epic #4391).
//
// Covers `AUDIT_KIND_REVIEW` (`../../audit/kind-review.ts`) — policy ingest, CI
// dispatch, CodeRabbit ingest, our own review verdicts and takeovers, the babysit
// merge driver, and the knowledge/backlog pipelines that ride beside them.
//
// ─── WHY MOST OF THIS FAMILY RENDERS FROM ID-SHAPED FIELDS ONLY ──────────────
//
// `TerminalRow.input`/`.output` are NULL whenever the writer set
// `visibleToMaintainer: false` — withheld by the query's `case when`, not by
// anything a renderer chooses (`wire/terminal.ts` § `TerminalRow`). MEASURED
// against every writer in this family (2026-09-18, one `grep -rn
// "AuditEventKind.<Name>"` per kind, cited per entry below): more than half of
// these 40 kinds write `visibleToMaintainer: false`, so their payload is simply
// absent on the row this module reads, regardless of what the writer put in it.
// A renderer that assumed otherwise would work in a unit test built from a hand
// -written row and crash — or worse, silently render nothing useful — on the
// real one.
//
// `fleet.git_token.minted`/`.refused` is the kind called out by name in project
// review because it is the highest-value credential the platform hands out and
// the row's own docblock (`kind-review.ts`) says the wire "deliberately
// collapses" its refusal reason: this renderer does not special-case it beyond
// what every other withheld-payload kind already gets — it reads NOTHING from
// `row.input`/`row.output` and renders from `row.repoFullName` and the row's own
// ids, which is the same treatment `ci.job.no_capacity` or `kb.page_upserted`
// gets. There is no branch here whose behaviour depends on whether the payload
// happens to be visible, because for this kind it never is.
//
// ─── `babysit.transition` IS THE ONE DYNAMIC EXCEPTION ───────────────────────
//
// Every other kind in this family fixes `visibleToMaintainer` at ONE writer
// call site. `babysit.transition` does not: `babysitTransitionAudit`
// (`apps/worker/src/babysit/wiring.ts`) passes `row.visibleToMaintainer`
// straight through from the driver's decision (true only on an `escalate`
// hop), while `watch_anchored` and `followup_minted` are always `false` and
// `repo_stand_down` is always `true`. So THIS renderer, alone in the family,
// branches on `ctx.row.visibleToMaintainer` before trusting `input.event` —
// every other kind either always has the payload or never does.
//
// ─── LEVELS THAT AREN'T INFO/OK/WARN/FAIL ─────────────────────────────────────
//
// `decision` is used for the rows the wire's own doc calls out — "a policy
// gate, an approval, a takeover" (`wire/terminal.ts` § `TerminalLevel`):
// `ci.policy.decision`, `coderabbit.enqueue.decision`, `review.takeover`,
// `review.nothing_reviewable`, `babysit.transition`, `review.check_observed`,
// `review.check_settled`. All seven are the platform
// grading itself or acting on someone else's behalf without being asked — the
// rows an operator scans for, not routine narration.

import {
  attemptParams,
  type TerminalFailRenderContext,
  type TerminalFailRendered,
  type TerminalRenderContext,
  type TerminalRendered,
  type TerminalSubject,
  terminalRendererGroup,
} from '../../../wire/terminal';
import { ACCOUNT_VERBS_REVIEW } from './account-verb';
import { TERMINAL_RENDERERS_REVIEW_CLOSE_LOOP } from './review-close-loop';
import { TERMINAL_RENDERERS_REVIEW_KNOWLEDGE } from './review-knowledge';
import { TERMINAL_RENDERERS_REVIEW_OUTBOX } from './review-outbox';
import {
  numField,
  numOpt,
  prSubject,
  rec,
  refsFor,
  repoSubject,
  strOpt,
  strOr,
} from './review-payload';

// ─── policy.* (`policy-ingest.ts`, all four kinds `visibleToMaintainer: true`) ─
//
// `appendPolicyAudit` (`apps/worker/src/consumers/policy-ingest.ts:401`) writes
// `input: { repo, sourceSha? }` on all four, and `output: { errors }` on the
// validation-failure arm only — the other three carry no `output` at all.

function policySourceSha(ctx: TerminalRenderContext): string | null {
  return strOpt(rec(ctx.row.input)['sourceSha']);
}

function policyValidationFailed(ctx: TerminalFailRenderContext): TerminalFailRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const errors = rec(ctx.row.output)['errors'];
  const errorCount = Array.isArray(errors) ? errors.length : 0;
  const firstError = Array.isArray(errors) && typeof errors[0] === 'string' ? errors[0] : null;
  return {
    line: {
      key: 'terminal.policy.validation_failed',
      params: {
        ...attemptParams(ctx.attempt),
        repo: repo ?? 'unknown repo',
        errorCount,
        ...(firstError !== null ? { firstError } : {}),
      },
    },
    subject: repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

// ─── ci.* (arbiter + log lanes) — all `visibleToMaintainer: false` except none ─
//
// `TaskAssigned`'s CI arm, `CiJobNoCapacity`, `CiPermissionMissing` (all three
// `apps/worker/src/wiring/mothership.ts`), `CiLogRecorded`/`CiLogUploadFailed`
// (`apps/worker/src/fleet/ci-log.ts`), `CiPolicyDecision`/`InternalRepoSkipped`
// (`apps/worker/src/wiring/mothership.ts` + `apps/worker/src/review/wiring.ts`)
// are ALL `visibleToMaintainer: false` — payload withheld on every row this
// renders. (`ci.job.placed` had no writer at all and is deleted with its
// renderer — plan 101 sweep 2026-10-01, finding 14-16.)

function ciGeneric(key: `terminal.${string}`, ctx: TerminalRenderContext): TerminalRendered {
  const repo = ctx.row.repoFullName;
  return {
    line: { key, params: { repo: repo ?? 'unknown repo' } },
    subject: repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

function ciLogUploadFailed(ctx: TerminalFailRenderContext): TerminalFailRendered {
  const repo = ctx.row.repoFullName;
  return {
    line: {
      key: 'terminal.ci.log.upload_failed',
      params: { ...attemptParams(ctx.attempt), repo: repo ?? 'unknown repo' },
    },
    subject: repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

// ─── coderabbit.* (all `visibleToMaintainer: true`) ──────────────────────────
//
// `appendSnapshotAudit` (`coderabbit-ingest.ts`) writes
// `input: { repo, sourceSha?, source, reason, warnings, error? }`.
// `coderabbitProbeAudit` (`review/wiring.ts`) writes
// `input: { deliveryId, repo, seenAt, reason }`.
// `coderabbitDecisionAudit` (`review/wiring.ts`) writes
// `input: { deliveryId, repo, prNumber, headSha?, disposition, configSource, reason }`.

function coderabbitSnapshotPersisted(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  return {
    line: {
      key: 'terminal.coderabbit.snapshot.persisted',
      params: {
        repo: repo ?? 'unknown repo',
        reason: strOr(input['reason'], 'ingested'),
        warnings: numField(input['warnings'], 0),
      },
    },
    subject: repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

function coderabbitProbeUpdated(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  return {
    line: {
      key: 'terminal.coderabbit.probe.updated',
      params: {
        repo: repo ?? 'unknown repo',
        seenAt: strOr(input['seenAt'], ctx.row.ts.toISOString()),
      },
    },
    subject: repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

function coderabbitEnqueueDecision(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(input['prNumber']);
  return {
    line: {
      key: 'terminal.coderabbit.enqueue.decision',
      params: {
        repo: repo ?? 'unknown repo',
        disposition: strOr(input['disposition'], 'unknown'),
        reason: strOr(input['reason'], 'unknown'),
      },
    },
    subject: prSubject(repo, pr, ctx.row.sessionId),
    refs: refsFor({ repo, pr, sessionId: ctx.row.sessionId }),
  };
}

// ─── review.* (depth_unservable, takeover — `visibleToMaintainer: true`) ─────

function reviewDepthUnservable(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(input['prNumber']);
  return {
    line: {
      key: 'terminal.review.depth_unservable',
      params: {
        repo: repo ?? 'unknown repo',
        reason: strOr(input['reason'], 'no server enrolled'),
      },
    },
    subject: prSubject(repo, pr, ctx.row.sessionId),
    refs: refsFor({
      repo,
      pr,
      taskId: strOpt(input['taskId']),
      sessionId: ctx.row.sessionId,
    }),
  };
}

function reviewTakeover(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(input['prNumber']);
  return {
    line: {
      key: 'terminal.review.takeover',
      params: {
        repo: repo ?? 'unknown repo',
        vendor: strOr(input['vendor'], 'unknown'),
        signal: strOr(input['signal'], 'unknown'),
      },
    },
    subject: prSubject(repo, pr, ctx.row.sessionId),
    refs: refsFor({ repo, pr, sessionId: ctx.row.sessionId }),
  };
}

/** `review.nothing_reviewable` — writer `nothingReviewableAudit` (`apps/worker`), always visible. */
function reviewNothingReviewable(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const pr = numOpt(input['prNumber']);
  return {
    line: {
      key: 'terminal.review.nothing_reviewable',
      params: { repo: repo ?? 'unknown repo', head: strOr(input['headSha'], 'unknown') },
    },
    subject: prSubject(repo, pr, ctx.row.sessionId),
    refs: refsFor({ repo, pr, sessionId: ctx.row.sessionId }),
  };
}

function reviewCheckSettled(ctx: TerminalRenderContext): TerminalRendered {
  // #4435: this draft's `input` carries `taskId`/`accountId`/`phase`/
  // `observedStatus` only — no `repo` or `prNumber` (`review/check-settle.lane.ts`
  // `reviewCheckSettleAuditPort`). The row's OWN `repoFullName` (from the session
  // join, independent of the payload) is the only repo this renderer can show.
  const input = rec(ctx.row.input);
  const taskId = strOpt(input['taskId']);
  const repo = ctx.row.repoFullName;
  const subject: TerminalSubject =
    taskId !== null
      ? { kind: 'task', id: taskId, label: taskId }
      : repoSubject(repo, ctx.row.sessionId);
  return {
    line: {
      key: 'terminal.review.check_settled',
      params: {
        phase: strOr(input['phase'], 'unknown'),
        observedStatus: strOr(input['observedStatus'], 'unknown'),
      },
    },
    subject,
    refs: refsFor({ repo, taskId, sessionId: ctx.row.sessionId }),
  };
}

function reviewCheckObserved(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const taskId = strOpt(input['taskId']);
  const repo = ctx.row.repoFullName;
  return {
    line: {
      key: 'terminal.review.check_observed',
      params: {
        state: strOr(input['state'], 'unknown'),
        observedStatus: strOr(input['observedStatus'], 'unknown'),
      },
    },
    subject:
      taskId === null
        ? repoSubject(repo, ctx.row.sessionId)
        : { kind: 'task', id: taskId, label: taskId },
    refs: refsFor({ repo, taskId, sessionId: ctx.row.sessionId }),
  };
}

// ─── babysit.transition — the one dynamic-visibility kind in this family ─────
//
// `repo_stand_down` is always `visibleToMaintainer: true`; `hop` carries the
// driver's own `escalate` flag through row.visibleToMaintainer (true only on
// escalation); `watch_anchored`/`followup_minted` are always `false`. So this
// is the only render in the family that must check `ctx.row.visibleToMaintainer`
// itself rather than trusting a fixed writer decision.

function babysitTransition(ctx: TerminalRenderContext): TerminalRendered {
  const repo = ctx.row.repoFullName;
  if (!ctx.row.visibleToMaintainer) {
    // Payload withheld on this row (a routine hop, a watch anchor, or a
    // follow-up mint) — generic, ID-shaped only, same treatment every other
    // withheld row in this family gets.
    return {
      line: { key: 'terminal.babysit.transition', params: { repo: repo ?? 'unknown repo' } },
      subject: repoSubject(repo, ctx.row.sessionId),
      refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
    };
  }
  const input = rec(ctx.row.input);
  const event = strOr(input['event'], 'hop');
  const rowRepo = strOpt(input['repo']) ?? repo;
  const pr = numOpt(input['prNumber']);
  const taskId = strOpt(input['taskId']);
  const params: Record<string, string | number> = { repo: rowRepo ?? 'unknown repo', event };
  if (event === 'hop') params['action'] = strOr(input['action'], 'unknown');
  return {
    line: { key: 'terminal.babysit.transition', params },
    subject: prSubject(rowRepo, pr, ctx.row.sessionId),
    refs: refsFor({ repo: rowRepo, pr, taskId, sessionId: ctx.row.sessionId }),
  };
}

// ─── gh.issue_opened (`visibleToMaintainer: true`) ───────────────────────────

function ghIssueOpened(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const issueNumber = numOpt(output['issue_number']);
  const outcome = output['reused'] === true ? 'reused' : 'opened';
  const label =
    issueNumber !== null && repo !== null ? `${repo}#${issueNumber}` : (repo ?? 'unknown repo');
  return {
    line: {
      key: 'terminal.gh.issue_opened',
      params: {
        repo: repo ?? 'unknown repo',
        outcome,
        ...(issueNumber !== null ? { issueNumber } : {}),
      },
    },
    subject:
      issueNumber !== null
        ? { kind: 'issue', id: label, label }
        : repoSubject(repo, ctx.row.sessionId),
    refs: refsFor({
      repo,
      issue: issueNumber,
      taskId: strOpt(input['taskId']),
      sessionId: ctx.row.sessionId,
    }),
  };
}

// ─── session.reaped / session.redriven (`visibleToMaintainer: true`) ────────

function sessionReaped(ctx: TerminalFailRenderContext): TerminalFailRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const label = repo ?? ctx.row.sessionId;
  return {
    line: {
      key: 'terminal.session.reaped',
      params: {
        ...attemptParams(ctx.attempt),
        repo: repo ?? 'unknown repo',
        silentMinutes: numField(output['silentMinutes'], 0),
      },
    },
    subject: { kind: 'session', id: ctx.row.sessionId, label },
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

function sessionRedriven(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  const label = repo ?? ctx.row.sessionId;
  return {
    line: { key: 'terminal.session.redriven', params: { repo: repo ?? 'unknown repo' } },
    subject: { kind: 'session', id: ctx.row.sessionId, label },
    refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
  };
}

// ─── integration.used (`visibleToMaintainer: true`) ──────────────────────────
//
// `buildIntegrationAudit` (`apps/api/src/deps/integrations.ts`) writes
// `input: { integrationKind, verb, sessionId }`, `output: { outcome }`. No
// `repo` on this row at all — an integration is account-scoped, not repo-scoped.

function integrationUsed(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const kind = strOr(input['integrationKind'], 'integration');
  return {
    line: {
      key: 'terminal.integration.used',
      params: {
        integrationKind: kind,
        verb: strOr(input['verb'], 'none'),
        outcome: strOr(output['outcome'], 'success'),
      },
    },
    // `id` is the row's own session, not the integration kind name — a
    // hardcoded id would merge every account's Slack (or any other
    // integration) rows into one subject (`wire/terminal.ts`'s
    // `TerminalSubject` contract, round-2 review). `kind` is still the
    // human-readable label.
    subject: { kind: 'session', id: ctx.row.sessionId, label: kind },
    refs: refsFor({ sessionId: ctx.row.sessionId }),
  };
}

// ─── byok.provider_disagreement (`visibleToMaintainer: true`) ───────────────

function byokProviderDisagreement(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const keyId = strOpt(input['keyId']);
  const label = strOpt(output['label']) ?? keyId ?? 'unknown';
  return {
    line: {
      key: 'terminal.byok.provider_disagreement',
      params: {
        provider: strOr(input['provider'], 'unknown'),
        status: strOr(output['status'], 'unknown'),
        decision: strOr(output['decision'], 'unknown'),
      },
    },
    // `label` may fall back to the literal 'unknown' (a display placeholder,
    // sanctioned by `wire/terminal.ts`'s `TerminalSubject` doc); `id` may not —
    // when there is no real key id, this falls back to the session, not the
    // string 'unknown' (round-2 review; the writer always sets `keyId` today,
    // so this arm is latent, not live).
    subject:
      keyId !== null
        ? { kind: 'key', id: keyId, label }
        : { kind: 'session', id: ctx.row.sessionId, label },
    refs: refsFor({ sessionId: ctx.row.sessionId }),
  };
}

// ─── fleet.git_token.minted / .refused — SECURITY-SENSITIVE, see module note ─
//
// Both `visibleToMaintainer: false` (`git-token-audit.ts`), always: `row.input`
// and `row.output` are null on every row of these two kinds this renderer will
// ever see, so there is nothing to branch on even in principle. The `reason`
// the wire deliberately collapses (`no_claim` / `not_your_repo` / …) lives one
// layer below this — in the withheld payload — and this renderer never reaches
// for it, withheld or not. The one real id the row carries is the session's,
// so the subject IS the session — not a `box` wearing a session id (#4445).

function fleetGitTokenLine(
  key: `terminal.${string}`,
  ctx: TerminalRenderContext,
): TerminalRendered {
  return {
    line: { key, params: {} },
    subject: { kind: 'session', id: ctx.row.sessionId, label: 'fleet credential request' },
    refs: refsFor({ sessionId: ctx.row.sessionId }),
  };
}

// ─── vault.reread (`visibleToMaintainer: true`) ──────────────────────────────

function vaultReread(ctx: TerminalRenderContext): TerminalRendered {
  const input = rec(ctx.row.input);
  const output = rec(ctx.row.output);
  const taskId = strOpt(input['taskId']);
  const repo = strOpt(input['repo']) ?? ctx.row.repoFullName;
  return {
    line: {
      key: 'terminal.vault.reread',
      params: { repo: repo ?? 'unknown repo', entryCount: numField(output['entryCount'], 0) },
    },
    // `id` falls back to the session, not the literal 'unknown', when there is
    // no real task id (round-2 review; the writer always sets `taskId` today,
    // so this arm is latent, not live).
    subject:
      taskId !== null
        ? { kind: 'task', id: taskId, label: taskId }
        : { kind: 'session', id: ctx.row.sessionId, label: 'unknown' },
    refs: refsFor({
      repo,
      taskId,
      boxId: strOpt(input['runnerId']),
      sessionId: ctx.row.sessionId,
    }),
  };
}

// ─── fleet_alert.* (all three `visibleToMaintainer: false`) ─────────────────
//
// `writeAlertAudit` (`fleet-alerts/wiring.ts`) writes `visibleToMaintainer:
// false` on every one of the three outcomes. `alertKind`/`dedupeKey` live in
// the withheld `input`, so this family cannot say WHICH alert — only that one
// fired, on which account.

function fleetAlertLine(key: `terminal.${string}`, ctx: TerminalRenderContext): TerminalRendered {
  return {
    line: { key, params: {} },
    // `id` is the row's own session, not the constant string 'fleet-alerts' — a
    // hardcoded id would collapse every account's fleet alerts into one subject
    // (`wire/terminal.ts`'s `TerminalSubject` contract, round-2 review). The
    // human-readable label is unaffected.
    subject: { kind: 'session', id: ctx.row.sessionId, label: 'fleet alerts' },
    refs: refsFor({ sessionId: ctx.row.sessionId }),
  };
}

// ─── the group ────────────────────────────────────────────────────────────────

/**
 * This family's own entries, named and exported so a partition test can prove
 * this table and {@link TERMINAL_RENDERERS_REVIEW_KNOWLEDGE}'s don't overlap —
 * same reasoning as `dispatch.ts`'s `DISPATCH_OWN` / `maintenance.ts`'s
 * `MAINTENANCE_CORE`: the composed export below spreads both into ONE
 * `terminalRendererGroup` call, so a key in both is not `ts(1117)` — the later
 * spread (knowledge) would win silently, and neither the whole-vocabulary
 * partition test (the same total either way) nor "one entry per kind" (the same
 * total either way) could catch it.
 */
const REVIEW_OWN = {
  'policy.validation_failed': {
    level: 'fail',
    actor: 'system',
    render: policyValidationFailed,
  },
  'policy.activated': {
    level: 'ok',
    actor: 'system',
    render: (ctx: TerminalRenderContext): TerminalRendered => {
      const repo = strOpt(rec(ctx.row.input)['repo']) ?? ctx.row.repoFullName;
      const sourceSha = policySourceSha(ctx);
      return {
        line: {
          key: 'terminal.policy.activated',
          params: { repo: repo ?? 'unknown repo', ...(sourceSha !== null ? { sourceSha } : {}) },
        },
        subject: repoSubject(repo, ctx.row.sessionId),
        refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
      };
    },
  },
  'policy.deleted': {
    level: 'info',
    actor: 'system',
    render: (ctx: TerminalRenderContext): TerminalRendered => {
      const repo = strOpt(rec(ctx.row.input)['repo']) ?? ctx.row.repoFullName;
      return {
        line: { key: 'terminal.policy.deleted', params: { repo: repo ?? 'unknown repo' } },
        subject: repoSubject(repo, ctx.row.sessionId),
        refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
      };
    },
  },
  'policy.stale_rejected': {
    level: 'warn',
    actor: 'system',
    render: (ctx: TerminalRenderContext): TerminalRendered => {
      const repo = strOpt(rec(ctx.row.input)['repo']) ?? ctx.row.repoFullName;
      return {
        line: { key: 'terminal.policy.stale_rejected', params: { repo: repo ?? 'unknown repo' } },
        subject: repoSubject(repo, ctx.row.sessionId),
        refs: refsFor({ repo, sessionId: ctx.row.sessionId }),
      };
    },
  },
  'ci.job.no_capacity': {
    level: 'warn',
    actor: 'system',
    render: (ctx: TerminalRenderContext) => ciGeneric('terminal.ci.job.no_capacity', ctx),
  },
  'ci.permission_missing': {
    level: 'warn',
    actor: 'system',
    render: (ctx: TerminalRenderContext) => ciGeneric('terminal.ci.permission_missing', ctx),
  },
  'ci.log.recorded': {
    level: 'ok',
    actor: 'system',
    render: (ctx: TerminalRenderContext) => ciGeneric('terminal.ci.log.recorded', ctx),
  },
  'ci.log.upload_failed': {
    level: 'fail',
    actor: 'system',
    render: ciLogUploadFailed,
  },
  // Platform-lane retention sweep (`prune-ci-logs.lane.ts`): no repo surface, no
  // tenant to anchor on, nothing an operator reads as a console line — the
  // account-fenced query can never even admit this row (`account_id` is null on
  // every platform-lane write). Pure internal bookkeeping.
  'ci.log.pruned': 'omit',
  'ci.policy.decision': {
    level: 'decision',
    actor: 'system',
    render: (ctx: TerminalRenderContext) => ciGeneric('terminal.ci.policy.decision', ctx),
  },
  // The CI wiring dropping a cached runner-group id GitHub no longer knows. It names no
  // task, run or pull request for a console line to hang on, and the next placement
  // re-resolves the group; the row is for the audit log.
  'ci.runner_group.forgotten': 'omit',
  // The CI wiring writing which repositories the account's runner group serves: account
  // configuration on GitHub's side, no task, run or pull request for a console line.
  'ci.runner_group.selection_set': 'omit',
  'internal.repo.skipped': {
    level: 'info',
    actor: 'system',
    render: (ctx: TerminalRenderContext) => ciGeneric('terminal.internal.repo.skipped', ctx),
  },
  'coderabbit.snapshot.persisted': {
    level: 'info',
    actor: 'system',
    render: coderabbitSnapshotPersisted,
  },
  'coderabbit.probe.updated': {
    level: 'info',
    actor: 'system',
    render: coderabbitProbeUpdated,
  },
  'coderabbit.enqueue.decision': {
    level: 'decision',
    actor: 'reviewer',
    render: coderabbitEnqueueDecision,
  },
  'review.depth_unservable': {
    level: 'warn',
    actor: 'reviewer',
    render: reviewDepthUnservable,
  },
  'review.takeover': {
    level: 'decision',
    actor: 'reviewer',
    render: reviewTakeover,
  },
  'review.nothing_reviewable': {
    level: 'decision',
    actor: 'reviewer',
    render: reviewNothingReviewable,
  },
  'babysit.transition': {
    level: 'decision',
    actor: 'babysitter',
    render: babysitTransition,
  },
  'gh.issue_opened': {
    level: 'ok',
    actor: 'system',
    render: ghIssueOpened,
  },
  // Platform-lane housekeeping over an internal idempotency ledger
  // (`stuck-reconciliation.lane.ts`): `visibleToMaintainer: false` by the
  // writer's own comment ("the maintainer has no action to take on one released
  // claim"), and platform-anchored so it never reaches an org's fenced tail
  // anyway.
  'reconciliation.expired': 'omit',
  'session.reaped': {
    level: 'fail',
    actor: 'system',
    render: sessionReaped,
  },
  'session.redriven': {
    level: 'ok',
    actor: 'system',
    render: sessionRedriven,
  },
  'integration.used': {
    level: 'info',
    actor: 'mcp',
    render: integrationUsed,
  },
  // The error-hygiene pass's rows. Read where they are written for: the pass summary on
  // `GET /v1/integrations` (`hygiene_last_pass`) and the session's own chain. A terminal
  // line and its `terminal.*` key ship when a reader asks for one; omit is honest until then.
  'error_tracker.mutated': 'omit',
  'error_hygiene.pass': 'omit',
  'byok.provider_disagreement': {
    level: 'warn',
    actor: 'system',
    render: byokProviderDisagreement,
  },
  'fleet.git_token.minted': {
    level: 'ok',
    actor: 'system',
    render: (ctx: TerminalRenderContext) =>
      fleetGitTokenLine('terminal.fleet.git_token.minted', ctx),
  },
  'fleet.git_token.refused': {
    level: 'warn',
    actor: 'system',
    render: (ctx: TerminalRenderContext) =>
      fleetGitTokenLine('terminal.fleet.git_token.refused', ctx),
  },
  'vault.reread': {
    level: 'info',
    actor: 'system',
    render: vaultReread,
  },
  'review.check_observed': { level: 'decision', actor: 'reviewer', render: reviewCheckObserved },
  'review.check_settled': {
    level: 'decision',
    actor: 'reviewer',
    render: reviewCheckSettled,
  },
  'fleet_alert.delivered': {
    level: 'ok',
    actor: 'system',
    render: (ctx: TerminalRenderContext) => fleetAlertLine('terminal.fleet_alert.delivered', ctx),
  },
  'fleet_alert.suppressed': {
    level: 'info',
    actor: 'system',
    render: (ctx: TerminalRenderContext) => fleetAlertLine('terminal.fleet_alert.suppressed', ctx),
  },
  'fleet_alert.failed': {
    level: 'fail',
    actor: 'system',
    render: (ctx: TerminalFailRenderContext) =>
      ({
        line: { key: 'terminal.fleet_alert.failed', params: { ...attemptParams(ctx.attempt) } },
        subject: { kind: 'session', id: ctx.row.sessionId, label: 'fleet alerts' },
        refs: refsFor({ sessionId: ctx.row.sessionId }),
      }) satisfies TerminalFailRendered,
  },
  // Internal usage-credit bookkeeping for the KB gardener (`visibleToMaintainer:
  // false`) — the fleet's own retrieval-tuning signal, not something a
  // maintainer reads or acts on.
  'kb.citations_uncredited': 'omit',
} as const;

/** For the partition test only — see the doc above {@link REVIEW_OWN}. */
export function reviewOwnKeys(): readonly string[] {
  return Object.keys(REVIEW_OWN);
}

export const TERMINAL_RENDERERS_REVIEW = terminalRendererGroup({
  ...REVIEW_OWN,
  ...TERMINAL_RENDERERS_REVIEW_KNOWLEDGE,
  ...TERMINAL_RENDERERS_REVIEW_CLOSE_LOOP,
  ...TERMINAL_RENDERERS_REVIEW_OUTBOX,
  ...ACCOUNT_VERBS_REVIEW,
});
